/**
 * I canali YouTube consigliati, variante per variante.
 *
 * Contenuto REDAZIONALE, non interfaccia: vive qui e non nei file di lingua
 * apposta. Il test `rules-i18n-parity` impone parita' esatta delle chiavi
 * `rules.*` fra le otto lingue — se la descrizione di un canale fosse una
 * chiave i18n, aggiungere un canale vorrebbe dire scrivere otto traduzioni o
 * lasciare la CI rossa, cioe' un vincolo d'interfaccia messo su un
 * aggiornamento puramente editoriale. Il precedente nel repo e'
 * `src/content/legal/<lang>/*.md`: contenuti per lingua, fuori dai JSON.
 *
 * ⚠️ TypeScript PURO. Niente React, niente `window`, niente `import.meta.glob`:
 * questo modulo viene caricato anche dal prerender SSR
 * (`scripts/prerender.mjs` → `vite.ssrLoadModule` → `RulesPrerenderRoot`), che
 * cuoce le pagine regole in HTML statico per otto lingue.
 *
 * ⚠️ Le pagine regole sono HTML statico: aggiungere o togliere un canale
 * richiede una nuova build e un redeploy, non basta scrivere qui.
 */
import { GameVariant } from '@draughts/shared';
import type { RulesLang } from '../seo/routes';

export interface VariantChannel {
  /**
   * Identificativo kebab-case stabile: chiave di React, id nei test, e
   * proprieta' dell'evento di analytics. NON e' l'handle, che puo' cambiare
   * senza che il canale cambi.
   */
  id: string;
  /** Nome proprio del canale. Non si traduce, mai. */
  name: string;
  /** L'handle con la chiocciola, mostrato come metadato. */
  handle: `@${string}`;
  /*
   * L'INDIRIZZO NON STA QUI. Vive in `CANALI_PARTNER` (@draughts/shared),
   * perche' lo legge anche il rimbalzo del server che conta i clic: tenerlo in
   * due posti vorrebbe dire due elenchi da allineare a mano. Qui resta il
   * contenuto redazionale, che il server non guarda mai.
   */
  /** Bandiera emoji, nella stessa forma di `spec.ui.flagEmoji`. */
  flag: string;
  /** Lingua parlata nei video. Non e' per forza una delle otto dell'app. */
  lang: string;
  /**
   * Una riga di presentazione. `en` e' obbligatoria e fa da ripiego; le altre
   * sono facoltative — una pagina olandese senza `nl` mostra l'inglese, che per
   * una riga di testo e' un prezzo accettabile (spesso il canale E' in inglese).
   */
  desc?: { en: string } & Partial<Record<RulesLang, string>>;
  /**
   * Predisposizione, non ancora usata: se una collocazione diventasse
   * compensata, le linee guida di Google impongono `rel="sponsored"` e una
   * dichiarazione visibile. Finche' i consigli sono redazionali resta assente.
   */
  sponsored?: boolean;
}

/**
 * I canali, per id. Sono qui e non dentro la mappa per variante perche' un
 * canale puo' servirne piu' d'una — chi commenta l'internazionale copre spesso
 * anche la brasiliana — e duplicarne il record vorrebbe dire aggiornarlo in due
 * punti.
 *
 * Una variante che non compare qui sotto non mostra la sezione affatto: nessun
 * titolo su una griglia vuota. Modello di una voce:
 *
 * 'dammen-nl': {
 *   id: 'dammen-nl',
 *   name: 'Dammen NL',
 *   handle: '@dammennl',
 *   flag: '🇳🇱',
 *   lang: 'nl',
 *   desc: {
 *     en: 'Tournament coverage and endgame studies, in Dutch.',   // max ~90 caratteri
 *     nl: 'Toernooiverslagen en eindspelstudies.',
 *     it: 'Cronache di torneo e studi di finale, in olandese.',
 *   },
 * },
 */
export const CHANNELS: Record<string, VariantChannel> = {
  /*
   * ⚠️ UNA VOCE DI PROVA, non un partner: l'handle non esiste e il link e'
   * rotto per chi lo clicca. Serve a tenere viva la sezione sulla pagina della
   * dama italiana mentre si cerca un canale italiano vero, e va SOSTITUITA,
   * non affiancata. Finche' e' qui il nome dice da solo che e' un segnaposto,
   * cosi' nessuno la scambia per un consiglio nostro.
   */
  'esempio-italiana': {
    id: 'esempio-italiana',
    name: 'Esempio · Dama italiana',
    handle: '@esempio-dama-italiana',
    flag: '\u{1F1EE}\u{1F1F9}',
    lang: 'it',
    desc: {
      en: 'Placeholder entry — to be replaced with a real partner channel.',
      it: 'Voce di prova: da sostituire con un canale partner vero.',
      nl: 'Testvermelding: wordt vervangen door een echt partnerkanaal.',
      de: 'Platzhalter: wird durch einen echten Partnerkanal ersetzt.',
      fr: 'Entrée de test : à remplacer par une vraie chaîne partenaire.',
      es: 'Entrada de prueba: se sustituirá por un canal asociado real.',
      pl: 'Wpis testowy: do zastąpienia prawdziwym kanałem partnerskim.',
    },
  },
  /*
   * Il primo partner vero, e il primo scambio: lui ci porta spettatori col
   * link di campagna `brasildamas` (vedi la mappa `CAMPAGNE` in
   * `packages/server/src/metrics/rimbalzo.routes.ts`), noi lo elenchiamo qui.
   *
   * Nome e handle sono maiuscoli perche' lo sono DAVVERO: `@BRASILDAMAS` e' la
   * forma canonica che YouTube restituisce, non una nostra enfasi. L'handle
   * minuscolo funziona come indirizzo, ma qui vale quello canonico — e il test
   * dei dati pretende che `url` e `handle` dicano la stessa cosa.
   */
  'brasil-damas': {
    id: 'brasil-damas',
    name: 'BRASIL DAMAS',
    handle: '@BRASILDAMAS',
    flag: '\u{1F1E7}\u{1F1F7}',
    lang: 'pt-BR',
    desc: {
      en: 'Grandmaster games, openings, endgames and analysis, in Portuguese.',
      'pt-BR': 'Jogos de grandes mestres, aberturas, finais e análises.',
      it: 'Partite di grandi maestri, aperture, finali e analisi, in portoghese.',
      nl: 'Partijen van grootmeesters, openingen, eindspelen en analyses, in het Portugees.',
      de: 'Großmeisterpartien, Eröffnungen, Endspiele und Analysen, auf Portugiesisch.',
      fr: 'Parties de grands maîtres, ouvertures, finales et analyses, en portugais.',
      es: 'Partidas de grandes maestros, aperturas, finales y análisis, en portugués.',
      pl: 'Partie arcymistrzów, otwarcia, końcówki i analizy, po portugalsku.',
    },
  },
};

/**
 * Quali canali compaiono su quale pagina regole, e in quale ordine: il primo
 * della lista e' il primo della griglia.
 */
export const CHANNELS_BY_VARIANT: Partial<Record<GameVariant, string[]>> = {
  [GameVariant.ITALIAN]: ['esempio-italiana'],
  [GameVariant.BRAZILIAN]: ['brasil-damas'],
};

/** I canali di una variante, gia' risolti. Funzione pura: la chiama anche l'SSR. */
export function channelsFor(variant: GameVariant): VariantChannel[] {
  const ids = CHANNELS_BY_VARIANT[variant];
  if (!ids) return [];
  return ids.map((id) => CHANNELS[id]).filter((c): c is VariantChannel => c !== undefined);
}

/**
 * La descrizione nella lingua giusta.
 *
 * Il ripiego e' esplicito e locale — lingua esatta, poi il tag base ('pt-BR' →
 * 'pt', che pesca 'pt-BR' se c'e'), poi l'inglese — e non il `fallbackLng`
 * globale di i18next, che qui non vede nulla perche' il testo non passa da li'.
 */
export function channelDesc(channel: VariantChannel, lang: string): string | undefined {
  const desc = channel.desc;
  if (!desc) return undefined;
  const esatta = (desc as Record<string, string | undefined>)[lang];
  if (esatta) return esatta;
  const base = lang.split('-')[0];
  if (base) {
    const perBase = Object.entries(desc).find(([code]) => code.split('-')[0] === base);
    if (perBase?.[1]) return perBase[1];
  }
  return desc.en;
}
